"use client";

import { LANGUAGES } from "@/lib/types";

interface HeatmapProps {
  data: Record<string, Record<string, number>>; // { ckpt: { lang: wer } }
  title: string;
}

function getColor(wer: number): string {
  if (wer <= 15) return "#22c55e";
  if (wer <= 25) return "#86efac";
  if (wer <= 35) return "#fbbf24";
  if (wer <= 45) return "#fb923c";
  if (wer <= 55) return "#f87171";
  return "#ef4444";
}

function getTextColor(wer: number): string {
  if (wer <= 15) return "#000";
  if (wer <= 25) return "#000";
  return "#fff";
}

export default function LanguageHeatmap({ data, title }: HeatmapProps) {
  const checkpoints = Object.keys(data).sort((a, b) => {
    const numA = parseInt(a.replace(/\D/g, "")) || 0;
    const numB = parseInt(b.replace(/\D/g, "")) || 0;
    return numA - numB;
  });

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg p-5 overflow-x-auto">
      <h3 className="text-xs font-medium text-[var(--color-foreground)] tracking-wide mb-4">{title}</h3>
      <table className="w-full text-[11px]">
        <thead>
          <tr>
            <th className="text-left text-[var(--color-muted)] font-normal pb-2 pr-4 whitespace-nowrap">Language</th>
            {checkpoints.map((ckpt) => (
              <th key={ckpt} className="text-center text-[var(--color-muted)] font-normal pb-2 px-2 whitespace-nowrap">
                {ckpt}
              </th>
            ))}
            {checkpoints.length >= 2 && (
              <th className="text-center text-[var(--color-muted)] font-normal pb-2 px-2 whitespace-nowrap">Delta</th>
            )}
          </tr>
        </thead>
        <tbody>
          {LANGUAGES.map((lang) => {
            const values = checkpoints.map((ckpt) => data[ckpt]?.[lang] ?? 0);
            const delta = checkpoints.length >= 2
              ? values[values.length - 1] - values[0]
              : 0;
            return (
              <tr key={lang} className="border-t border-[var(--color-border)]">
                <td className="py-2 pr-4 text-[var(--color-foreground)] capitalize whitespace-nowrap">
                  {lang}
                </td>
                {values.map((v, i) => (
                  <td key={i} className="py-2 px-2 text-center">
                    <span
                      className="inline-block px-2 py-0.5 rounded text-[10px] font-medium tabular-nums"
                      style={{ backgroundColor: getColor(v), color: getTextColor(v) }}
                    >
                      {v.toFixed(1)}%
                    </span>
                  </td>
                ))}
                {checkpoints.length >= 2 && (
                  <td className="py-2 px-2 text-center">
                    <span
                      className={`text-[10px] font-medium tabular-nums ${
                        delta < 0 ? "text-[var(--color-success)]" : delta > 0 ? "text-[var(--color-danger)]" : "text-[var(--color-muted)]"
                      }`}
                    >
                      {delta > 0 ? "+" : ""}{delta.toFixed(1)}%
                    </span>
                  </td>
                )}
              </tr>
            );
          })}
          {/* Overall row */}
          <tr className="border-t-2 border-[var(--color-border-light)] font-medium">
            <td className="py-2 pr-4 text-[var(--color-foreground)]">Overall</td>
            {checkpoints.map((ckpt) => {
              const v = data[ckpt]?.["__overall__"] ?? 0;
              return (
                <td key={ckpt} className="py-2 px-2 text-center">
                  <span
                    className="inline-block px-2 py-0.5 rounded text-[10px] font-bold tabular-nums"
                    style={{ backgroundColor: getColor(v), color: getTextColor(v) }}
                  >
                    {v.toFixed(1)}%
                  </span>
                </td>
              );
            })}
            {checkpoints.length >= 2 && (() => {
              const first = data[checkpoints[0]]?.["__overall__"] ?? 0;
              const last = data[checkpoints[checkpoints.length - 1]]?.["__overall__"] ?? 0;
              const d = last - first;
              return (
                <td className="py-2 px-2 text-center">
                  <span className={`text-[10px] font-bold tabular-nums ${d < 0 ? "text-[var(--color-success)]" : d > 0 ? "text-[var(--color-danger)]" : "text-[var(--color-muted)]"}`}>
                    {d > 0 ? "+" : ""}{d.toFixed(1)}%
                  </span>
                </td>
              );
            })()}
          </tr>
        </tbody>
      </table>
      {/* Legend */}
      <div className="flex gap-3 mt-4 justify-end">
        {[
          { label: "< 15%", color: "#22c55e" },
          { label: "15-25%", color: "#86efac" },
          { label: "25-35%", color: "#fbbf24" },
          { label: "35-45%", color: "#fb923c" },
          { label: "45-55%", color: "#f87171" },
          { label: "> 55%", color: "#ef4444" },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: item.color }} />
            <span className="text-[9px] text-[var(--color-muted)]">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
